import React, { useState, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { utils, writeFile } from 'xlsx';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL, APP_NAME } from '../../utils';
import { formatDate } from '../../utils/functions';
import Add from './add';
import Update from './update';
import axios from 'axios';
import { useSelector } from 'react-redux';

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

export default function Stores() {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/stores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data?.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des showrooms :', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredItems = useMemo(() => {
    const search = searchInput.trim().toLowerCase();
    if (!search) return data || [];
    return (data || []).filter((item) => {
      const haystack = `${item.name || ''} ${item.specialite || ''} ${item.date_creation || ''}`.toLowerCase();
      return haystack.includes(search);
    });
  }, [data, searchInput]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aValue = a[key] ?? '';
      const bValue = b[key] ?? '';
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredItems, sortConfig]);

  const stats = useMemo(
    () => ({
      total: filteredItems.length,
    }),
    [filteredItems]
  );

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / perPage));
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const currentItems = sortedItems.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchInput]);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleRefresh = async () => {
    await fetchData();
    setCurrentPage(1);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Showrooms', 14, 16);
    doc.autoTable({
      head: [['Showroom', 'Spécialité', 'Date de création']],
      body: currentItems.map((item) => [item.name, item.specialite, formatDate(item.date_creation)]),
      startY: 24,
    });
    doc.save(`Showrooms ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const worksheet = utils.json_to_sheet(
      currentItems.map((item) => ({
        Showroom: item.name,
        Spécialité: item.specialite,
        'Date de création': formatDate(item.date_creation),
      }))
    );
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Showrooms');
    writeFile(workbook, `Showrooms ${APP_NAME}.xlsx`);
  };

  const colSpan = isRole13 ? 3 : 4;

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Organisation</p>
          <h1 className="duo-hero-title">Showrooms</h1>
          <p className="duo-hero-sub mt-2 mb-0">Gérez les showrooms et leurs spécialités.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des showrooms
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={() => window.print()} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              {!isRole13 && (
                <button type="button" className="duo-btn duo-btn--primary" onClick={() => setShowAddModal(true)}>
                  <i className="bi bi-plus" /> Nouveau
                </button>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher un showroom, une spécialité ou une date..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('name')}>
                    Showroom <SortIcon active={isSortActive('name')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('specialite')}>
                    Spécialité <SortIcon active={isSortActive('specialite')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('date_creation')}>
                    Date de création{' '}
                    <SortIcon active={isSortActive('date_creation')} direction={sortConfig.direction} />
                  </th>
                  {!isRole13 && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-bold">{item.name}</td>
                      <td>{item.specialite}</td>
                      <td className="text-nowrap">{formatDate(item.date_creation)}</td>
                      {!isRole13 && (
                        <td>
                          <div className="duo-table__actions">
                            <button
                              type="button"
                              title="Modifier"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              onClick={() => setEditingItem(item)}
                            >
                              <i className="bi bi-pencil-square" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={colSpan} className="duo-empty-row">
                      Aucun showroom correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentItems.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedItems.length)} sur {sortedItems.length}{' '}
                entrée{sortedItems.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      <Add open={showAddModal} onClose={() => setShowAddModal(false)} refreshData={handleRefresh} />
      <Update
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        item={editingItem}
        refreshData={handleRefresh}
      />
    </MainLayout>
  );
}
